import { SxProps, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { theme } from '@repo/ui/Theme';
import Image from 'next/image';
import { PropsWithChildren, Ref } from 'react';

const FLAG_TAIL_WIDTH_PX = 20;
export const BANNER_HEIGHT_FIXED_PX = 32;

const styles = {
  container: {
    containerType: 'inline-size',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  awardTextContainer: {
    backgroundColor: 'greens.light',
    display: 'flex',
    flexDirection: 'row',
  },
  awardText: {
    alignItems: 'center',
    color: 'greens.dark',
    display: 'flex',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
} as const satisfies Record<string, SxProps>;

type Props = PropsWithChildren<{
  variant?: 'row' | 'row-compact' | 'card';
  ref?: Ref<HTMLElement>;
  sx?: SxProps;
}>;

export function BestOfAwardsRoundupBanner({ children, ref, sx, variant = 'row' }: Props) {
  if (variant === 'card') {
    return (
      <CardVariant ref={ref} sx={sx}>
        {children}
      </CardVariant>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.container,
        ...sx,
      }}
    >
      <Box
        sx={{
          backgroundColor: 'greens.light',
          display: 'inline-flex',
          height: variant === 'row' ? BANNER_HEIGHT_FIXED_PX : 'auto',
          paddingLeft: 3,
        }}
      >
        <Image
          width={variant === 'row-compact' ? 26 : 32}
          height={variant === 'row-compact' ? 32 : 40}
          src="https://www.nerdwallet.com/cdn/banking/icons/best_of_awards_2026_badge.svg"
          alt="NerdWallet Best-Of Awards"
          unoptimized
          style={{ objectFit: 'cover' }}
        />
      </Box>
      <Box
        sx={{
          ...styles.awardTextContainer,
          columnGap: 3,
          height: variant === 'row' ? BANNER_HEIGHT_FIXED_PX : 'auto',
          paddingLeft: 3,
          py: '4px',
          // Adds the flag tail design
          clipPath: `polygon(0 0, 100% 0, calc(100% - ${FLAG_TAIL_WIDTH_PX}px / 2) 50%, 100% 100%, 0 100%)`,
          paddingRight: `${FLAG_TAIL_WIDTH_PX + 8}px`,
          [theme.containerQueries.down('sm')]: {
            width: variant === 'row-compact' ? '100%' : 'auto',
          },
        }}
      >
        <Typography variant="textExtraSmall" sx={styles.awardText}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

function CardVariant({ children, ref, sx }: Omit<Props, 'variant'>) {
  return (
    <Box
      ref={ref}
      sx={{
        ...styles.container,
        alignItems: 'start',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
        }}
      >
        <Image
          width={45}
          height={56}
          src="https://www.nerdwallet.com/cdn/banking/icons/best_of_awards_2026_badge.svg"
          alt="NerdWallet Best-Of Awards"
          unoptimized
          style={{ objectFit: 'cover' }}
        />
      </Box>
      <Box
        sx={{
          ...styles.awardTextContainer,
          borderBottomRightRadius: 4,
          borderTopRightRadius: 4,
          minHeight: 40,
          px: 3,
          py: 1,
          [theme.containerQueries.down('sm')]: {
            width: '100%',
          },
        }}
      >
        <Typography
          variant="accent"
          sx={{
            ...styles.awardText,
            lineHeight: '150%',
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
